/** @jsxImportSource @emotion/react */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import * as S from "./MultiSelectBase.styles";
import { useMultiSelectPanel } from "./useMultiSelectPanel";
import { SelectAllToolbar } from "./SelectAllToolbar";
import { getResource } from "../../language/resources/getResource";
import { ItemKey, Accessor, readWithAccessor } from "./types";
import { useRangeSelection } from "./useRangeSelection";
import { css, SerializedStyles } from "@emotion/react";
import filterIcon from "../../assets/icons/filter.svg";

const iconButtonCss = css`
  cursor: pointer;
  pointer-events: auto;
`;

export type MultiSelectFilterProps<T> = {
  resources: Record<string, any>;
  label: string;
  items: T[];

  keyAccessor: Accessor<T, ItemKey>;
  labelAccessor: Accessor<T, string>;

  selectedKeys: ItemKey[];
  onChange: (keys: ItemKey[]) => void;

  placeholder?: string;
  disabled?: boolean;

  showSearch?: boolean;
  searchPlaceholder?: string;
  /** Render the search field in the control instead of at the top of the panel. */
  searchInControl?: boolean;

  showClear?: boolean;
  clearLabel?: string;

  requiredAsterisk?: boolean;

  controlWidth?: number | string;
  controlMaxWidth?: number | string;
  controlHeight?: number | string;
  iconSize?: number | string;

  panelMaxWidth?: number | string;
  panelMaxHeight?: number;

  rootCss?: SerializedStyles;
  buttonCss?: SerializedStyles;
  /** Style overrides for the dropdown panel. */
  panelCss?: SerializedStyles;
  placeholderTextCss?: SerializedStyles;
  selectedTextCss?: SerializedStyles;
  iconCss?: SerializedStyles;
  dropdownIconSrc?: string;
};

export function MultiSelectFilter<T>({
  resources,
  label,
  items,
  keyAccessor,
  labelAccessor,
  selectedKeys,
  onChange,

  placeholder,
  disabled = false,

  showSearch = true,
  searchPlaceholder = "Search…",
  searchInControl = false,

  showClear = true,
  clearLabel = "Clear",

  requiredAsterisk = false,

  controlWidth,
  controlMaxWidth,
  controlHeight,
  iconSize = 24,

  panelMaxWidth,
  panelMaxHeight = 240,

  rootCss,
  buttonCss,
  panelCss,
  placeholderTextCss,
  selectedTextCss,
  iconCss,
  dropdownIconSrc,
}: MultiSelectFilterProps<T>) {
  const { open, setOpen, btnRef, panelRef, panelMinWidth } =
    useMultiSelectPanel();

  const selectAllRef = useRef<HTMLInputElement | null>(null);

  const [search, setSearch] = useState("");

  // Reopening starts from the full list.
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);
  const popupId = useId();

  const normalized = useMemo(
    () =>
      items.map((item) => ({
        key: readWithAccessor(item, keyAccessor),
        label: readWithAccessor(item, labelAccessor),
      })),
    [items, keyAccessor, labelAccessor],
  );

  // Filter
  const filtered = useMemo(() => {
    if (!showSearch || !search.trim()) return normalized;
    const q = search.toLowerCase();
    return normalized.filter((i) => i.label.toLowerCase().includes(q));
  }, [normalized, search, showSearch]);

  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const { onCheckboxClick } = useRangeSelection({
    orderedKeys: useMemo(() => filtered.map((i) => i.key), [filtered]),
    selectedKeys,
    onChange,
    resetKey: open,
  });

  const isFiltered = showSearch && search.trim().length > 0;
  const shownCount = filtered.length;
  const selectedCount = selectedKeys.length;

  const allSelected =
    shownCount > 0 && filtered.every((i) => selectedSet.has(i.key));

  const searching = open && showSearch && searchInControl;

  // Outside clicks must not refocus the trigger: that would pull focus off whatever was clicked.
  const restoreFocusOnClose = useRef(false);

  const closeAndRefocus = () => {
    restoreFocusOnClose.current = true;
    setOpen(false);
  };

  useEffect(() => {
    if (open || !restoreFocusOnClose.current) return;
    restoreFocusOnClose.current = false;
    btnRef.current?.focus();
  }, [open, btnRef]);

  // Button text
  const noneSelected = selectedKeys.length === 0;

  const buttonText = noneSelected
    ? (placeholder ?? label)
    : normalized
        .filter((i) => selectedSet.has(i.key))
        .map((i) => i.label)
        .join(", ");

  // Select‑all toggle
  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      onChange(
        Array.from(new Set([...selectedKeys, ...filtered.map((i) => i.key)])),
      );
    } else {
      const inScope = new Set(filtered.map((i) => i.key));
      onChange(selectedKeys.filter((k) => !inScope.has(k)));
    }
  };

  const handleClear = () => {
    onChange([]);
    setSearch("");
  };

  return (
    <S.Root
      css={css`
        ${rootCss}
      `}
    >
      <S.Button
        /* A <button> may not contain the search <input>. */
        as={searching ? "div" : undefined}
        ref={btnRef as any}
        css={buttonCss}
        /* preventDefault: the wrapping <label> forwards a second click that reopens the panel. */
        onClick={(e) => {
          e.preventDefault();
          if (disabled) return;
          /* Anywhere but the text field closes -- the chevron included. */
          if (searching) closeAndRefocus();
          else setOpen((o) => !o);
        }}
        /* is-open: while open the trigger is a div, so :focus cannot match it. */
        className={
          [disabled ? "is-disabled" : "", open ? "is-open" : ""]
            .filter(Boolean)
            .join(" ") || undefined
        }
        controlWidth={controlWidth}
        controlMaxWidth={controlMaxWidth}
        controlHeight={controlHeight}
        iconSize={iconSize}
        title={searching ? undefined : buttonText}
      >
        <S.ButtonBody>
          {searching ? (
            <S.ControlSearch onClick={(e) => e.stopPropagation()}>
              <S.ControlSearchInput
                autoFocus
                css={selectedTextCss}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && closeAndRefocus()}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
              />
              {search && (
                <S.ClearButton
                  type="button"
                  onClick={() => setSearch("")}
                  title={getResource(resources, "ACCESS_OVERVIEW_CLEAR_SEARCH")}
                >
                  ×
                </S.ClearButton>
              )}
            </S.ControlSearch>
          ) : (
            <S.ButtonText
              isPlaceholder={noneSelected}
              css={noneSelected ? placeholderTextCss : selectedTextCss}
            >
              {buttonText}
              {noneSelected && requiredAsterisk && <S.RequiredMarker />}
            </S.ButtonText>
          )}
        </S.ButtonBody>

        <S.FilterIcon
          iconSize={iconSize}
          /* stopPropagation keeps the container's handler from undoing this. */
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (disabled) return;
            if (open) closeAndRefocus();
            else setOpen(true);
          }}
          css={iconButtonCss}
        >
          <img
            src={dropdownIconSrc ?? filterIcon}
            alt=""
            draggable={false}
            width={iconSize}
            height={iconSize}
            css={noneSelected ? iconCss : undefined}
          />
        </S.FilterIcon>
      </S.Button>

      {open && (
        <S.Panel
          css={panelCss}
          ref={panelRef}
          id={popupId}
          minW={panelMinWidth}
          maxW={panelMaxWidth}
        >
          {/* ---------- search ---------- */}
          {showSearch && !searchInControl && (
            <S.Search>
              <S.SearchFieldWrapper>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                />
                {search && (
                  <S.ClearButton
                    type="button"
                    onClick={() => setSearch("")}
                    title={getResource(
                      resources,
                      "ACCESS_OVERVIEW_CLEAR_SEARCH",
                    )}
                  >
                    ×
                  </S.ClearButton>
                )}
              </S.SearchFieldWrapper>
            </S.Search>
          )}

          {/* ---------- select all / counts ---------- */}
          <SelectAllToolbar
            selectAllRef={selectAllRef}
            allSelected={allSelected}
            selectedCount={selectedCount}
            shownCount={shownCount}
            isFiltered={isFiltered}
            onToggle={toggleSelectAll}
            onClear={handleClear}
            showClear={showClear}
            clearLabel={clearLabel}
          />

          <S.Divider />

          {/* ---------- options ---------- */}
          <S.OptionsScroll panelMaxHeight={panelMaxHeight}>
            {filtered.map((i) => (
              <S.Option key={String(i.key)}>
                <S.Check>
                  <input
                    type="checkbox"
                    checked={selectedSet.has(i.key)}
                    // Selection is handled in onClick (to read the Shift modifier);
                    // onChange just satisfies React's controlled-input contract.
                    onChange={() => {}}
                    onClick={(e) => onCheckboxClick(i.key, e)}
                  />
                  <span>{i.label}</span>
                </S.Check>
              </S.Option>
            ))}

            {filtered.length === 0 && (
              <S.Empty>
                {getResource(resources, "ACCESS_OVERVIEW_NO_OPTIONS")}
              </S.Empty>
            )}
          </S.OptionsScroll>
        </S.Panel>
      )}
    </S.Root>
  );
}
